import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { ShieldAlert, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import AdminUnlockDialog from "@/components/AdminUnlockDialog";
import AdminShell from "@/components/admin/AdminShell";

/**
 * Gate the entire admin area:
 *   1. Must be logged in.
 *   2. Email must be on ADMIN_EMAILS allow-list (is_admin_eligible=true).
 *   3. Session must be unlocked (is_admin=true).
 *
 * Normal users never see this — they get redirected back to their customer
 * dashboard. Eligible admins see the unlock dialog on entry.
 */
export default function AdminGuard({ children }) {
  const { user, loading, isAdmin, isAdminEligible, refresh } = useAuth();
  const [unlockOpen, setUnlockOpen] = useState(false);

  useEffect(() => {
    if (!loading && user && isAdminEligible && !isAdmin) {
      setUnlockOpen(true);
    }
  }, [loading, user, isAdminEligible, isAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--bg-0)] text-[color:var(--text-2)]">
        <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading admin…
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Non-admin users: redirect to customer dashboard. Never reveal admin exists.
  if (!isAdminEligible) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--bg-0)] text-[color:var(--text-1)] px-4">
        <div
          className="max-w-md text-center rounded-2xl border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.04)] p-8"
          data-testid="admin-locked-state"
        >
          <div className="mx-auto h-12 w-12 rounded-full bg-[color:rgba(255,138,61,0.18)] flex items-center justify-center text-[color:var(--accent-warm)] mb-4">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <div className="font-display text-xl font-semibold">Admin console is locked</div>
          <div className="text-sm text-[color:var(--text-2)] mt-2 leading-relaxed">
            Enter your admin passcode to access the owner dashboard. This page
            is invisible to regular customers.
          </div>
          <button
            type="button"
            onClick={() => setUnlockOpen(true)}
            className="mt-5 inline-flex items-center justify-center rounded-md bg-[color:var(--accent-warm)] hover:opacity-90 text-[color:var(--bg-0)] font-semibold px-4 py-2 text-sm"
            data-testid="admin-open-unlock"
          >
            Unlock admin console
          </button>
          <AdminUnlockDialog
            open={unlockOpen}
            onOpenChange={setUnlockOpen}
            onUnlocked={async () => {
              await refresh();
            }}
          />
        </div>
      </div>
    );
  }

  return <AdminShell>{children}</AdminShell>;
}
